import {isFeatureEnabled} from '@github-ui/feature-flags'
import {announce} from '@github-ui/aria-live'
import {Banner} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useMemo, useRef, useState} from 'react'

import {useBranchAliveUpdates} from '../../hooks/use-branch-alive-updates'
import {useTaskChangeset} from '../../hooks/use-task-changeset'
import {useTaskDiffs} from '../../hooks/use-task-diffs'
import type {APITaskArtifact} from '../../types/artifact'
import type {Pull} from '../../types/pull'
import {changesetFilesToDiffData} from '../../utils/changeset-to-diff-data'
import {ActionBarTrigger} from './AhpTaskChatInputActionBar/ActionBarTrigger'
import {AhpTaskChatInputActionBar} from './AhpTaskChatInputActionBar/AhpTaskChatInputActionBar'
import {ArtifactItem} from './AhpTaskChatInputActionBar/ArtifactItem'
import {DiffTriggerButton} from './AhpTaskChatInputActionBar/DiffTriggerButton'
import {ItemsPanel} from './AhpTaskChatInputActionBar/ItemsPanel'
import {PullRequestButton} from './PullRequestButton'
import styles from './AhpTaskChatInputActions.module.css'

const EMPTY_ARTIFACTS: APITaskArtifact[] = []

export interface AhpTaskChatInputActionsProps {
  pull?: Pull
  repoNwo: string
  taskId: string
  baseRef: string
  headRef: string
  branchAliveChannel?: string
  isSessionInProgress?: boolean
  onViewDiff?: () => void
  artifacts?: APITaskArtifact[]
}

export function AhpTaskChatInputActions({
  pull,
  repoNwo,
  taskId,
  baseRef,
  headRef,
  branchAliveChannel,
  isSessionInProgress = false,
  onViewDiff,
  artifacts = EMPTY_ARTIFACTS,
}: AhpTaskChatInputActionsProps) {
  const [prError, setPrError] = useState<string | null>(null)
  const [artifactsOpen, setArtifactsOpen] = useState(false)
  const artifactsTriggerRef = useRef<HTMLButtonElement>(null)
  const artifactsCloseButtonRef = useRef<HTMLButtonElement>(null)
  const artifactsWasOpenRef = useRef(false)
  const actionBarRef = useRef<HTMLDivElement>(null)
  const {data: legacyDiffs, refetch: refetchDiffs} = useTaskDiffs(repoNwo, taskId, baseRef, headRef, {
    // Managed-sandbox sessions have no real branch/head_ref (empty strings) — skip the REST call.
    enabled: !!baseRef && !!headRef,
  })

  // Additive signal for established AHP sessions, which never populate the legacy REST endpoint.
  // Flag passed into the hook so it never subscribes at all while the rollout flag is off.
  const changesetRolloutEnabled = isFeatureEnabled('copilot_mission_control_ahp_diff')
  const changeset = useTaskChangeset(changesetRolloutEnabled)
  const hasChangesetDiffs =
    changeset.available &&
    (changeset.persisted || changeset.status === 'ready' || changeset.status === 'recomputing') &&
    changeset.files.length > 0
  const hasDiffs = hasChangesetDiffs || Boolean(legacyDiffs?.files.length)
  const changesetDiffs = useMemo(() => changesetFilesToDiffData(changeset.files), [changeset.files])
  // "Create pull request" intentionally stays gated on the legacy REST signal only — a
  // managed-sandbox session can't create one from stale/no branch info.
  const hasLegacyDiffs = Boolean(legacyDiffs?.files.length)
  const showLegacyDiffs = hasLegacyDiffs && !hasChangesetDiffs
  const fallbackDiffs =
    changeset.persisted && hasChangesetDiffs ? changesetDiffs : showLegacyDiffs ? legacyDiffs : undefined
  const savedError = !hasDiffs && changeset.persisted ? changeset.error : undefined
  useEffect(() => {
    if (savedError) announce(savedError)
  }, [savedError])

  if (artifactsOpen && artifacts.length === 0) {
    setArtifactsOpen(false)
  }

  useBranchAliveUpdates(branchAliveChannel, () => {
    void refetchDiffs()
  })

  useEffect(() => {
    if (artifactsOpen) {
      artifactsCloseButtonRef.current?.focus()
      artifactsWasOpenRef.current = true
    } else if (artifactsWasOpenRef.current) {
      const focusTarget =
        artifactsTriggerRef.current ??
        actionBarRef.current?.querySelector<HTMLElement>('button:not([disabled]), a[href]')
      focusTarget?.focus()
      artifactsWasOpenRef.current = false
    }
  }, [artifactsOpen])

  const savedErrorBanner = savedError ? (
    <Banner variant="warning" title="Changed file information is unavailable" description={savedError} />
  ) : null
  if (!hasLegacyDiffs && !fallbackDiffs && artifacts.length === 0) return savedErrorBanner

  return (
    <div className={clsx(styles.AhpTaskChatInputActions, artifactsOpen && styles.AhpTaskChatInputActionsExpanded)}>
      <AhpTaskChatInputActionBar ref={actionBarRef}>
        {fallbackDiffs && (
          <DiffTriggerButton
            additions={fallbackDiffs.additions}
            deletions={fallbackDiffs.deletions}
            onViewDiff={onViewDiff}
          />
        )}
        {hasLegacyDiffs && (
          <PullRequestButton
            pullUrl={pull?.url}
            repoNwo={repoNwo}
            taskId={taskId}
            baseRef={baseRef}
            headRef={headRef}
            disabled={isSessionInProgress}
            onError={setPrError}
          />
        )}
        {artifacts.length > 0 && (
          <ActionBarTrigger
            ref={artifactsTriggerRef}
            aria-expanded={artifactsOpen}
            count={artifacts.length}
            onClick={() => setArtifactsOpen(open => !open)}
          >
            Artifacts
          </ActionBarTrigger>
        )}
      </AhpTaskChatInputActionBar>
      {artifactsOpen && (
        <ItemsPanel
          title="Artifacts"
          items={artifacts.map(artifact => (
            <ArtifactItem key={getArtifactId(artifact)} artifact={artifact} repoNwo={repoNwo} />
          ))}
          closeButtonRef={artifactsCloseButtonRef}
          onClose={() => setArtifactsOpen(false)}
        />
      )}
      {savedErrorBanner}
      {prError ? (
        <Banner
          className={styles.ErrorBanner}
          variant="critical"
          title="Error creating pull request"
          hideTitle
          description={prError}
          onDismiss={() => setPrError(null)}
        />
      ) : null}
    </div>
  )
}

AhpTaskChatInputActions.displayName = 'AhpTaskChatInputActions'

function getArtifactId(artifact: APITaskArtifact): string {
  switch (artifact.type) {
    case 'branch':
      return `branch-${artifact.data.head_ref}`
    case 'github_resource':
      return `github-resource-${artifact.data.global_id}`
    case 'azure_devops_resource':
      return `azure-devops-resource-${artifact.data.url}`
  }
}
